import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  Divider,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { loginSchema } from "./validations";
import { loginApi, verifyMagicLinkApi } from "../../services/authService";
import { loginUser } from "../../store/reducers/auth";
import { whiteLogo, FooterLogo } from "../../assets";
import { ErrorText } from "../../components";
import { Logs } from "../../services";
import { notification } from "antd";

// Custom color constants
const primaryBlue = "#0e3584";

export function Login({ handleForgotPassword, handleOtpOrMagicLink }) {
  const form = useForm({
    resolver: yupResolver(loginSchema),
    mode: "all",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = form;
  const [invalidCredentials, setInvalidCredentials] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const queryParams = new URLSearchParams(window.location.search);
  const token = queryParams.get("token");
  const [isVerifyingMagicLink, setIsVerifyingMagicLink] = useState(!!token);

  useEffect(() => {
    const sourceUrl = queryParams.get("source");
    if (token) {
      verifyMagicLogin(token, sourceUrl);
    }
  }, []);

  const verifyMagicLogin = async (token, sourceUrl) => {
    try {
      const res = await verifyMagicLinkApi(token);
      if (res.status === 200) {
        dispatch(loginUser(res.data));
        if(!res.data?.isImpersonating){
          notification.success({
            message: "Login Successful",
            description: "Magic link verified successfully.",
          });
        }
        if(sourceUrl){
          sessionStorage.setItem("source", sourceUrl);
        }
        if (res.data?.user?.role?.toLowerCase() === "vkyc auditor") {
          navigate("/product-catalogue", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      }
    } catch (error) {
      notification.error({
        message: "Magic Link Invalid or Expired",
        description: error?.response?.data?.error?.message || "Please login again using OTP or Email link.",
      });
      Logs(error);
      setIsVerifyingMagicLink(false);
    }
  };
  const handleLogin = async (data) => {
    setIsLoading(true);
    try {
      const email = data?.email.toLowerCase();
      const res = await loginApi(email, data?.password);
      if (res.status === 200) {
        dispatch(loginUser(res.data));
        if(res.data?.user?.role?.toLowerCase() ==="vkyc auditor"){
          navigate("/product-catalogue", { replace: true });
        }else{
          navigate("/", { replace: true });
        }
      }
    } catch (error) {
      setInvalidCredentials(true);
      setApiErrorMessage(error?.response?.data?.error?.message || "");
      Logs(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Reset invalid credentials message on form change
  useEffect(() => {
    if (watch("email") === "" || watch("password") === "") {
      setInvalidCredentials(false);
      setApiErrorMessage("");
    }
  }, [watch("email"), watch("password")]);

  if (isVerifyingMagicLink) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          width: "100vw",
          bgcolor: primaryBlue,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={48} sx={{ color: "#fff" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        bgcolor: primaryBlue,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        m: 0,
      }}
    >
      {/* Left panel */}
      <Box
        sx={{
          width: { xs: "0", md: "50%" },
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          pl: { md: 12, lg: 20 },
          color: "#fff",
          height: "100vh",
          bgcolor: primaryBlue,
        }}
      >
        <Box component="img" src={whiteLogo} sx={{ width: 240, mb: 2 }} />
        <Typography sx={{ fontSize: 18, opacity: 0.9, mb: 4, color: "#fff" }}>
        It is a unified financial data and services platform that provides ready-to-use APIs across lending, identity, compliance, and other financial workflows, enabling businesses to quickly integrate, scale, and innovate without rebuilding core infrastructure
        </Typography>
          <Stack spacing={2}>
        <Typography sx={{ fontSize: 18, opacity: 0.9, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          • Compliance-first onboarding
        </Typography>
        <Typography sx={{ fontSize: 18, opacity: 0.9, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          • Faster fraud detection
        </Typography>
        <Typography sx={{ fontSize: 18, opacity: 0.9, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          • Access multiple APIs seamlessly
        </Typography>
        <Typography sx={{ fontSize: 18, opacity: 0.9, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
          • Trusted by leading BFSIs
        </Typography>
      </Stack>
      </Box>

      {/* Right panel - Login form */}
      <Box
        sx={{
          width: { xs: "100%", sm: "90%", md: "50%", lg: "35%" },
          minHeight: { xs: "100vh", md: "auto" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "transparent",
        }}
      >
        <Paper
          elevation={4}
          sx={{
            borderRadius: "20px",
            px: { xs: 2, sm: 8 },
            py: { xs: 4, sm: 8 },
            width: "100%",
            maxWidth: 400,
            boxSizing: "border-box",
            minHeight: 250,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography variant="h4" fontWeight={600} textAlign="center" mb={5}>
            Secure access to Finhub
          </Typography>
          <form onSubmit={handleSubmit(handleLogin)} autoComplete="off">
            <Stack spacing={2}>
              {invalidCredentials && (
                <>
                  <ErrorText>
                    The email or password you entered is incorrect.
                  </ErrorText>
                  {apiErrorMessage && (
                    <ErrorText>{apiErrorMessage}</ErrorText>
                  )}
                </>
              )}
              <TextField
                fullWidth
                label="Email address"
                id="email"
                type="email"
                placeholder="Enter your email"
                {...register("email")}
                error={!!errors.email}
                helperText={errors.email?.message}
                InputLabelProps={{ shrink: true }}
                InputProps={{ style: { fontSize: 22 } }}
                autoComplete="email"
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label="Password"
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                {...register("password")}
                error={!!errors.password}
                helperText={errors.password?.message}
                InputLabelProps={{ shrink: true }}
                autoComplete="current-password"
                InputProps={{
                  style: { fontSize: 22 },
                  endAdornment: (
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      aria-label="toggle password visibility"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                    </IconButton>
                  ),
                }}
              />
              <Button
                sx={{ alignSelf: "flex-end", textTransform: "none", fontSize: "12px" }}
                onClick={handleForgotPassword}
                size="small"
              >
                Forgot password?
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ fontSize: 18, fontWeight: 600, py: 1.5, mt: 1 }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : "Sign In"}
              </Button>

              <Divider sx={{ my: 5, fontSize: "16px", fontWeight: 500 }}>or</Divider>

                <Button
                  variant="outlined"
                  onClick={handleOtpOrMagicLink}
                  disabled={isLoading}
                  sx={{
                    fontSize: 16,
                    fontWeight: 600,
                    py: 1.5,
                    borderWidth: 2,
                    "&:hover": { borderWidth: 2 },
                  }}
                >
                  Login via OTP or Link
                </Button>
            </Stack>
          </form>
          <Typography mt={3} sx={{ textAlign: "center", fontSize: "16px" }}>
            Don&apos;t Have Account?{" "}
            <Link
              to="https://www.habilelabs.io/contact-us"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#1976d2",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                e.target.style.color = "#1976d2";
                e.target.style.textDecoration = "underline";
              }}
              onMouseLeave={(e) => {
                e.target.style.color = "#1976d2";
                e.target.style.textDecoration = "none";
              }}
            >
              Contact us
            </Link>
          </Typography>
          <Box mt={3} textAlign="center">
            <Typography variant="body2" color="text.secondary" fontSize={16}>
              powered by{" "}
              <a
                href="https://www.habilelabs.io/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#2ab6e5", fontWeight: 500, textDecoration: "none" }}
              >
                HabileLabs
              </a>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}